import { AuthStorage } from "utils/auth-storage";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Input, Tooltip, message } from "antd";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { ProjectServices } from "apis/Services/ProjectService";
import styles from "./ProcurementLogPage.module.css";
import {
  mapProcurementImportRowForApi,
  normalizeProcurementDateValue,
  normalizeProcurementImportRow,
  normalizeProcurementImportRows,
} from "./procurementImportUtils";

ModuleRegistry.registerModules([AllCommunityModule]);

const modules = [AllCommunityModule];

const IMPORT_SELECTION_FIELD = "__importReviewSelection";
const DATE_COLUMN_FIELDS = new Set([
  "contractIssuedRelease",
  "submittalIssued",
  "submittalReturned",
  "dateRequiredOnSite",
  "subCommittedDeliveryDate",
  "actualDeliveryDate",
]);
const NUMERIC_ONLY_FIELDS = new Set([
  "approvalTime",
  "leadTime",
  "submittalPrepTime",
]);

const PROCUREMENT_IMPORT_REVIEW_COLUMNS = [
  {
    headerName: "Spec Section - Spec Section Name",
    field: "specSection",
    minWidth: 260,
    type: "text",
  },
  {
    headerName: "Submittal Number",
    field: "specNumber",
    minWidth: 170,
    type: "text",
  },
  { headerName: "Title", field: "specTitle", minWidth: 240, type: "text" },
  {
    headerName: "Revision",
    field: "specRevision",
    minWidth: 120,
    type: "text",
  },
  { headerName: "Type", field: "specType", minWidth: 150, type: "text" },
  {
    headerName: "Responsible Contractor",
    field: "responsibleContractor",
    minWidth: 220,
    type: "text",
  },
  {
    headerName: "Contract Issued Release",
    field: "contractIssuedRelease",
    minWidth: 190,
    type: "date",
  },
  {
    headerName: "Submittal Prep Time",
    field: "submittalPrepTime",
    minWidth: 170,
    type: "text",
  },
  {
    headerName: "Submittal Issued",
    field: "submittalIssued",
    minWidth: 160,
    type: "date",
  },
  {
    headerName: "Approval Time",
    field: "approvalTime",
    minWidth: 140,
    type: "text",
  },
  {
    headerName: "Submittal Returned",
    field: "submittalReturned",
    minWidth: 180,
    type: "date",
  },
  { headerName: "Lead Time", field: "leadTime", minWidth: 130, type: "text" },
  {
    headerName: "Schedule ID",
    field: "scheduleID",
    minWidth: 140,
    type: "text",
  },
  {
    headerName: "Date Required On Site",
    field: "dateRequiredOnSite",
    minWidth: 190,
    type: "date",
  },
  {
    headerName: "Sub Committed Delivery Date",
    field: "subCommittedDeliveryDate",
    minWidth: 220,
    type: "date",
  },
  {
    headerName: "Actual Delivery Date",
    field: "actualDeliveryDate",
    minWidth: 170,
    type: "date",
  },
];

function parseReviewDateValue(value) {
  const normalizedValue = normalizeProcurementDateValue(value);

  if (!normalizedValue) {
    return null;
  }

  const mmDdYyyyMatch = normalizedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (mmDdYyyyMatch) {
    const [, month, day, year] = mmDdYyyyMatch;
    const parsedDate = dayjs(`${year}-${month}-${day}`);
    return parsedDate.isValid() ? parsedDate : null;
  }

  return null;
}

function isStrictMmDdYyyy(value) {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return true;
  }

  const mmDdYyyyMatch = normalizedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!mmDdYyyyMatch) {
    return false;
  }

  const [, month, day, year] = mmDdYyyyMatch;
  const parsedDate = dayjs(`${year}-${month}-${day}`);

  return (
    parsedDate.isValid() && parsedDate.format("MM-DD-YYYY") === normalizedValue
  );
}

function isNumericOnlyValue(value) {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return true;
  }

  return /^-?\d+(\.\d+)?$/.test(normalizedValue);
}

function getCellValidationMessage(row, field) {
  const value = row?.[field];

  if (DATE_COLUMN_FIELDS.has(field) && !isStrictMmDdYyyy(value)) {
    return "Invalid date format. Use MM-DD-YYYY.";
  }

  if (NUMERIC_ONLY_FIELDS.has(field) && !isNumericOnlyValue(value)) {
    return "Only numeric values are allowed.";
  }

  return "";
}

function getRowValidationMessages(row) {
  return PROCUREMENT_IMPORT_REVIEW_COLUMNS.flatMap((columnConfig) => {
    const validationMessage = getCellValidationMessage(row, columnConfig.field);

    return validationMessage
      ? [`${columnConfig.headerName}: ${validationMessage}`]
      : [];
  });
}

function getMergedRowErrorMessages(row) {
  const localValidationMessages = getRowValidationMessages(row);
  const backendErrorMessages = Array.isArray(row?.errorMessages)
    ? row.errorMessages
    : [];

  return [...new Set([...localValidationMessages, ...backendErrorMessages])];
}

function getValidationCellStyle(row, field) {
  if (!getCellValidationMessage(row, field)) {
    return {
      backgroundColor: "",
      color: "",
    };
  }

  return {
    backgroundColor: "#fff2f0",
    color: "#cf1322",
  };
}

function getValidationRowStyle(row) {
  return getMergedRowErrorMessages(row).length > 0
    ? { backgroundColor: "#fffaf9" }
    : { backgroundColor: "" };
}

const TextEditor = ({ value, onValueChange, stopEditing }) => (
  <Input
    autoFocus
    variant="borderless"
    defaultValue={value}
    onChange={(event) => onValueChange(event.target.value)}
    onBlur={() => stopEditing()}
    onPressEnter={() => stopEditing()}
    style={{ width: "100%", height: "100%", fontSize: "13px" }}
  />
);

const ReviewDateEditor = ({ value, onValueChange, stopEditing }) => {
  const pickerValue = useMemo(() => parseReviewDateValue(value), [value]);

  return (
    <div className="procurementDateEditor">
      <DatePicker
        autoFocus
        open={true}
        allowClear
        inputReadOnly
        size="large"
        value={pickerValue}
        format="MM-DD-YYYY"
        onChange={(dateValue) => {
          onValueChange(dateValue ? dateValue.format("MM-DD-YYYY") : "");
          stopEditing();
        }}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            stopEditing();
          }
        }}
        popupStyle={{ zIndex: 2200 }}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

function getInitialSelectedView(acceptedRows, rejectedRows) {
  if (acceptedRows.length > 0) {
    return "accepted";
  }

  if (rejectedRows.length > 0) {
    return "issues";
  }

  return "all";
}

function getViewButtonClassName(selectedView, viewKey) {
  return `${styles.antBtn} ${
    selectedView === viewKey ? styles.quickFilterButtonActive : ""
  }`;
}

function ReviewProcurementLogFile({
  rejectedData = [],
  acceptedData = [],
  handleCancel,
  setAcceptedData,
  setRejectedData,
  onImportSuccess = () => {},
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
}) {
  const { company_id, id } = useParams();
  const normalizedAcceptedData = useMemo(
    () => normalizeProcurementImportRows(acceptedData),
    [acceptedData],
  );
  const normalizedRejectedData = useMemo(
    () => normalizeProcurementImportRows(rejectedData),
    [rejectedData],
  );
  const [selectedView, setSelectedView] = useState(() =>
    getInitialSelectedView(normalizedAcceptedData, normalizedRejectedData),
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const gridRef = useRef(null);

  const allData = useMemo(
    () => [...normalizedAcceptedData, ...normalizedRejectedData],
    [normalizedAcceptedData, normalizedRejectedData],
  );

  const tableData = useMemo(() => {
    if (selectedView === "accepted") {
      return normalizedAcceptedData;
    }

    if (selectedView === "issues") {
      return normalizedRejectedData;
    }

    return allData;
  }, [allData, normalizedAcceptedData, normalizedRejectedData, selectedView]);

  const columnDefs = useMemo(
    () => [
      {
        field: IMPORT_SELECTION_FIELD,
        colId: IMPORT_SELECTION_FIELD,
        headerName: "",
        headerCheckboxSelection: true,
        checkboxSelection: true,
        pinned: "left",
        lockPinned: true,
        lockPosition: "left",
        width: 108,
        minWidth: 108,
        maxWidth: 108,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        editable: false,
        cellRenderer: (params) => {
          const lineNumber = params.data?.lineCnt || params.node.rowIndex + 1;
          const errors = getMergedRowErrorMessages(params.data);
          const hasErrors = errors.length > 0;

          return (
            <div className={styles.importReviewSelectionCell}>
              <span className={styles.importReviewLineNumber}>
                {lineNumber}
              </span>
              {hasErrors ? (
                <Tooltip
                  title={
                    <div>
                      {errors.map((errorMessage, index) => (
                        <div key={`${errorMessage}-${index}`}>
                          • {errorMessage}
                        </div>
                      ))}
                    </div>
                  }
                  color="#1f1f1f"
                >
                  <ExclamationCircleOutlined
                    style={{
                      color: "#ff4d4f",
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              ) : null}
            </div>
          );
        },
      },
      ...PROCUREMENT_IMPORT_REVIEW_COLUMNS.map((columnConfig) => ({
        headerName: columnConfig.headerName,
        field: columnConfig.field,
        minWidth: columnConfig.minWidth,
        editable: true,
        cellEditor:
          columnConfig.type === "date" ? ReviewDateEditor : TextEditor,
        valueParser: (params) =>
          columnConfig.type === "date"
            ? normalizeProcurementDateValue(params.newValue)
            : String(params.newValue ?? "").trim(),
        valueFormatter:
          columnConfig.type === "date"
            ? (params) => normalizeProcurementDateValue(params.value)
            : undefined,
        tooltipValueGetter: (params) =>
          getCellValidationMessage(params.data, columnConfig.field) || null,
        cellStyle: (params) =>
          getValidationCellStyle(params.data, columnConfig.field),
      })),
      // {
      //   headerName: "Issues",
      //   field: "errorMessages",
      //   editable: false,
      //   minWidth: 280,
      //   autoHeight: true,
      //   wrapText: true,
      //   cellRenderer: (params) => {
      //     const errors = params.data?.errorMessages || [];

      //     if (!errors.length) {
      //       return (
      //         <span className={styles.importReviewNoIssuesLabel}>
      //           No issues
      //         </span>
      //       );
      //     }

      //     return (
      //       <div className={styles.importReviewIssuesList}>
      //         {errors.map((errorMessage, index) => (
      //           <div key={`${errorMessage}-${index}`}>• {errorMessage}</div>
      //         ))}
      //       </div>
      //     );
      //   },
      // },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: true,
    }),
    [],
  );

  useEffect(() => {
    if (selectedView !== "all") {
      return;
    }

    if (allData.length === 0) {
      return;
    }

    gridRef.current?.api?.refreshCells({ force: true });
  }, [allData.length, selectedView]);

  useEffect(() => {
    if (!gridRef.current?.api) {
      return;
    }

    gridRef.current.api.deselectAll();
    setSelectedRows([]);
    gridRef.current.api.refreshCells({ force: true });
  }, [selectedView, tableData]);

  const handleImport = () => {
    gridRef.current?.api?.stopEditing();
    setImporting(true);

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    const updatedRows = allData.map((row, index) =>
      normalizeProcurementImportRow(row, index),
    );

    const requestData = {
      company_id,
      project_id: id,
      code,
      loginEmail,
      saveFlag: "1",
      submittalRows: updatedRows.map(mapProcurementImportRowForApi),
    };
    const syncToken = beginSyncActivity?.("Importing procurement log");

    ProjectServices.saveUploadedProcurementLog(requestData)
      .then((res) => {
        const acceptedItemsCnt =
          Number(res?.acceptedItemsCnt) ||
          updatedRows.length - (res?.invalidItems?.length || 0);
        const invalidItems = normalizeProcurementImportRows(res?.invalidItems);

        if (invalidItems.length > 0) {
          if (acceptedItemsCnt > 0) {
            onImportSuccess?.();
          }

          message.warning(
            `${acceptedItemsCnt} rows were imported successfully. ${invalidItems.length} rows still contain issues and were not imported. Please review them below.`,
            6,
          );
          setAcceptedData([]);
          setRejectedData(invalidItems);
          setSelectedView("issues");
          finishSyncActivity?.(syncToken, "Procurement log import completed");
          return;
        }

        message.success("All rows imported successfully!");
        onImportSuccess?.();
        finishSyncActivity?.(syncToken, "Procurement log imported");
        handleCancel?.();
      })
      .catch((error) => {
        console.error(error);
        message.error(
          error?.message ||
            "Failed to import the procurement log. Please try again.",
        );
        failSyncActivity?.(
          syncToken,
          "Failed to import the procurement log. You may no longer be synced to the latest version.",
        );
      })
      .finally(() => {
        setImporting(false);
      });
  };

  const handleSelectionChanged = () => {
    const selected = gridRef.current?.api?.getSelectedRows?.() || [];
    setSelectedRows(selected);
  };

  const handleDeleteSelectedRows = () => {
    const selectedLineIds = new Set(
      selectedRows.map((row) => String(row.lineCnt)),
    );

    let nextAcceptedRows = normalizedAcceptedData;
    let nextRejectedRows = normalizedRejectedData;

    if (selectedView === "accepted") {
      nextAcceptedRows = normalizedAcceptedData.filter(
        (row) => !selectedLineIds.has(String(row.lineCnt)),
      );
    } else if (selectedView === "issues") {
      nextRejectedRows = normalizedRejectedData.filter(
        (row) => !selectedLineIds.has(String(row.lineCnt)),
      );
    } else {
      nextAcceptedRows = normalizedAcceptedData.filter(
        (row) => !selectedLineIds.has(String(row.lineCnt)),
      );
      nextRejectedRows = normalizedRejectedData.filter(
        (row) => !selectedLineIds.has(String(row.lineCnt)),
      );
    }

    setAcceptedData(nextAcceptedRows);
    setRejectedData(nextRejectedRows);
    message.success("Selected rows deleted successfully.");
  };

  return (
    <div className={`${styles.tableCard} ${styles.importReviewCard}`}>
      <div className={`${styles.quickBar} ${styles.importReviewQuickBar}`}>
        <div className={styles.importReviewToolbarLeft}>
          <div className={styles.importReviewHeadingGroup}>
            {/* <span className={styles.importReviewHeading}>
              Review uploaded submittals
            </span> */}
            {/* <span className={styles.importReviewSubheading}>
              Clean up any issues, adjust dates with the picker, then import the
              final rows.
            </span> */}
          </div>

          <div className={styles.importReviewViewButtons}>
            <Button
              className={getViewButtonClassName(selectedView, "all")}
              onClick={() => setSelectedView("all")}
            >
              All Rows
              <span
                className={`${styles.importReviewTabCount} ${
                  selectedView === "all"
                    ? styles.importReviewTabCountActive
                    : ""
                }`}
              >
                {allData.length}
              </span>
            </Button>

            <Button
              className={getViewButtonClassName(selectedView, "accepted")}
              onClick={() => setSelectedView("accepted")}
            >
              Accepted Rows
              <span
                className={`${styles.importReviewTabCount} ${
                  selectedView === "accepted"
                    ? styles.importReviewTabCountActive
                    : ""
                }`}
              >
                {normalizedAcceptedData.length}
              </span>
            </Button>

            <Button
              className={getViewButtonClassName(selectedView, "issues")}
              onClick={() => setSelectedView("issues")}
            >
              Rows with Issues
              <span
                className={`${styles.importReviewTabCount} ${
                  selectedView === "issues"
                    ? styles.importReviewTabCountActive
                    : ""
                }`}
              >
                {normalizedRejectedData.length}
              </span>
            </Button>
          </div>
        </div>

        <div className={styles.importReviewToolbarRight}>
          {/* <span className={styles.importReviewSummaryText}>
            Showing {tableData.length} rows in {selectedViewLabel}
          </span> */}

          {selectedRows.length > 0 ? (
            <span className={styles.selectionBadge}>
              {selectedRows.length} selected
            </span>
          ) : null}
        </div>
      </div>

      <div
        className={`${styles.gridWrapper} ${styles.importReviewGridWrapper}`}
      >
        <div
          className={`ag-theme-alpine w-100 h-100 ${styles.grid} ${styles.importReviewGrid}`}
        >
          <AgGridReact
            ref={gridRef}
            modules={modules}
            rowData={tableData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            suppressDragLeaveHidesColumns={true}
            animateRows={false}
            editType="singleCell"
            singleClickEdit={true}
            domLayout="normal"
            enableBrowserTooltips={true}
            tooltipShowDelay={100}
            headerHeight={60}
            rowHeight={48}
            overlayNoRowsTemplate="<span>No rows found in this view</span>"
            onSelectionChanged={handleSelectionChanged}
            getRowId={(params) =>
              String(
                params.data?.lineCnt ??
                  params.data?.entryID ??
                  params.data?.specTitle ??
                  params.rowIndex,
              )
            }
            getRowStyle={(params) => getValidationRowStyle(params.data)}
            onCellClicked={(params) => {
              if (params.colDef.field === IMPORT_SELECTION_FIELD) {
                const isSelected = params.node.isSelected();
                params.node.setSelected(!isSelected);
              }
            }}
            onCellValueChanged={(params) => {
              params.data[params.colDef.field] = DATE_COLUMN_FIELDS.has(
                params.colDef.field,
              )
                ? normalizeProcurementDateValue(params.newValue)
                : String(params.newValue ?? "").trim();

              gridRef.current?.api?.refreshCells({
                rowNodes: [params.node],
                force: true,
              });
              gridRef.current?.api?.redrawRows({
                rowNodes: [params.node],
              });
            }}
          />
        </div>
      </div>

      <div className={styles.importReviewFooter}>
        <div className={styles.importReviewFooterActions}>
          <Button className={styles.antBtn} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            danger
            className={styles.importReviewDangerButton}
            disabled={selectedRows.length < 1}
            onClick={handleDeleteSelectedRows}
          >
            <DeleteOutlined />
            Delete Selected
          </Button>
        </div>

        <Button
          type="primary"
          className={styles.importReviewPrimaryButton}
          loading={importing}
          onClick={handleImport}
          disabled={allData.length < 1}
        >
          Import Rows
        </Button>
      </div>
    </div>
  );
}

export default ReviewProcurementLogFile;
